/**
 * 浏览器端 WebSocket 连接管理器（对齐小程序 WebSocketManager）
 */
export default class WebSocketManager {
  constructor(options = {}) {
    this.url = options.url || ''
    this.token = options.token || ''
    this.heartbeatInterval = options.heartbeatInterval || 30000
    this.reconnectInterval = options.reconnectInterval || 3000
    this.maxReconnectAttempts = options.maxReconnectAttempts || 5
    this.autoReconnect = options.autoReconnect !== false

    this.ws = null
    this.isConnected = false
    this.isConnecting = false
    this.isReconnecting = false
    this.manualClose = false
    this.reconnectAttempts = 0
    this.heartbeatTimer = null
    this.reconnectTimer = null
    this.messageQueue = []
    this.eventListeners = {
      open: [],
      message: [],
      error: [],
      close: [],
      reconnect: []
    }
  }

  buildUrl() {
    if (!this.url) throw new Error('缺少 WebSocket 地址')
    const url = new URL(this.url)
    if (this.token) {
      url.searchParams.set('token', this.token)
    }
    return url.toString()
  }

  connect(token) {
    if (token) this.token = token
    if (this.isConnected || this.isConnecting) {
      return Promise.resolve()
    }
    if (!this.token) {
      return Promise.reject(new Error('缺少 token'))
    }

    this.manualClose = false
    this.isConnecting = true

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.buildUrl())
      } catch (error) {
        this.isConnecting = false
        this.handleError(error)
        reject(error)
        return
      }

      this.ws.onopen = () => {
        this.isConnected = true
        this.isConnecting = false
        this.isReconnecting = false
        this.reconnectAttempts = 0
        this.startHeartbeat()
        this.flushMessageQueue()
        this.emit('open')
        resolve()
      }

      this.ws.onmessage = (event) => {
        let data = event.data
        try {
          data = JSON.parse(event.data)
        } catch (e) {
          // 保留原始字符串
        }
        if (data && data.type === 'pong') return
        this.emit('message', data)
      }

      this.ws.onerror = (error) => {
        this.handleError(error)
        if (this.isConnecting) {
          this.isConnecting = false
          reject(error)
        }
      }

      this.ws.onclose = () => {
        this.isConnected = false
        this.isConnecting = false
        this.stopHeartbeat()
        this.ws = null
        this.emit('close')
        if (this.autoReconnect && !this.manualClose) {
          this.reconnect()
        }
      }
    })
  }

  send(data) {
    const message = typeof data === 'string' ? data : JSON.stringify(data)
    if (this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(message)
      return Promise.resolve(true)
    }
    this.messageQueue.push(data)
    return Promise.resolve(false)
  }

  flushMessageQueue() {
    if (!this.messageQueue.length) return
    const queue = [...this.messageQueue]
    this.messageQueue = []
    queue.forEach((item) => {
      this.send(item).catch(() => {})
    })
  }

  startHeartbeat() {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping' })
    }, this.heartbeatInterval)
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  reconnect() {
    if (this.manualClose) return
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return
    if (this.reconnectTimer) return

    this.isReconnecting = true
    this.reconnectAttempts += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect(this.token)
        .then(() => {
          this.emit('reconnect', { attempts: this.reconnectAttempts })
        })
        .catch(() => {
          this.isReconnecting = false
          this.reconnect()
        })
    }, this.reconnectInterval)
  }

  close() {
    this.manualClose = true
    this.autoReconnect = false
    this.stopHeartbeat()
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      try {
        this.ws.close()
      } catch (e) {
        // ignore
      }
      this.ws = null
    }
    this.isConnected = false
    this.isConnecting = false
    this.isReconnecting = false
  }

  updateConfig(config = {}) {
    if (config.url != null) this.url = config.url
    if (config.token != null) this.token = config.token
    if (config.heartbeatInterval != null) this.heartbeatInterval = config.heartbeatInterval
    if (config.reconnectInterval != null) this.reconnectInterval = config.reconnectInterval
    if (config.maxReconnectAttempts != null) this.maxReconnectAttempts = config.maxReconnectAttempts
    if (config.autoReconnect != null) this.autoReconnect = config.autoReconnect
  }

  getStatus() {
    return {
      isConnected: this.isConnected,
      isConnecting: this.isConnecting,
      isReconnecting: this.isReconnecting,
      reconnectAttempts: this.reconnectAttempts,
      messageQueueLength: this.messageQueue.length,
      url: this.url
    }
  }

  on(event, handler) {
    if (!this.eventListeners[event]) this.eventListeners[event] = []
    this.eventListeners[event].push(handler)
    return () => {
      this.eventListeners[event] = this.eventListeners[event].filter((fn) => fn !== handler)
    }
  }

  emit(event, payload) {
    const list = this.eventListeners[event] || []
    list.forEach((fn) => {
      try {
        fn(payload)
      } catch (e) {
        console.error('WebSocket listener error:', e)
      }
    })
  }

  handleError(error) {
    this.emit('error', error)
  }
}
